import React, { useEffect, useState } from 'react';
import { addShipment, type Shipment, type Status } from './Spedizioni';
import { X, ArrowRight, Search, Check, Loader2, CheckCircle2, Truck, MapPin, Package, User, Calendar, AlertCircle, PackageX, RotateCcw, Clock } from 'lucide-react';
const CARRIERS: {
  id: string;
  name: string;
  logo: string;
  placeholder: string;
}[] = [{
  id: 'fedex',
  name: 'FedEx',
  logo: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  placeholder: '770123456789'
}, {
  id: 'gls',
  name: 'GLS',
  logo: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  placeholder: 'GLS1234567890'
}, {
  id: 'ups',
  name: 'UPS',
  logo: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png',
  placeholder: '1Z999AA10123456784'
}, {
  id: 'brt',
  name: 'BRT',
  logo: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  placeholder: '012345678901'
}, {
  id: 'sda',
  name: 'SDA',
  logo: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  placeholder: '1234567890123'
}];
type Phase = 'input' | 'loading' | 'preview' | 'done';
type MappedStatus = 'CONSEGNATA' | 'IN_CONSEGNA' | 'IN_TRANSITO' | 'TRASMESSO' | 'GIACENZA' | 'RESO' | 'ALTRO';
type TrackingEvent = {
  date: string;
  time: string;
  location: string;
  rawStatus: string;
  mappedStatus: MappedStatus;
  note?: string;
};
type LoadedShipment = {
  id: string;
  carrierId: string;
  carrierName: string;
  tracking: string;
  trackingRecovered?: string;
  status: string;
  statusDate: string;
  statusTime: string;
  delivered: boolean;
  isReturn: boolean;
  giacenza: {
    numero: string;
    apertura: string;
    motivazione?: string;
    dispoRequired: boolean;
  } | null;
  pickupLocation?: string;
  events: TrackingEvent[];
  raw?: unknown;
};
const LOADING_STEPS = ['Connessione al corriere…', 'Recupero dettagli spedizione…', 'Sincronizzo eventi di tracking…', 'Calcolo ETA e route…'];
const AISAC_TRACKING_URL = 'https://api.aisac.io/v1/tracking-status/api.php';
const AISAC_TOKEN = '0394UF983JX043KD43KC9K3C9K3C';
function asString(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}
function capitalizeFirst(s: string): string {
  if (!s) return s;
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}
function mapStatus(raw: string): MappedStatus {
  const v = raw.toUpperCase();
  if (v === 'CONSEGNATA' || v === 'RESO') return v as MappedStatus;
  if (v === 'IN_CONSEGNA' || v === 'IN_TRANSITO' || v === 'TRASMESSO' || v === 'GIACENZA') {
    return v as MappedStatus;
  }
  return 'ALTRO';
}
function mapApiResponse(payload: unknown, carrierId: string, carrierName: string, tracking: string): LoadedShipment {
  const root = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
  const detailsRaw = Array.isArray(root.dettagli_spedizione) ? root.dettagli_spedizione as unknown[] : [];
  const events: TrackingEvent[] = detailsRaw.map(e => {
    if (!e || typeof e !== 'object') return null;
    const ev = e as Record<string, unknown>;
    const rawStatus = asString(ev.stato);
    return {
      date: asString(ev.data),
      time: asString(ev.ora),
      location: asString(ev.luogo),
      rawStatus,
      mappedStatus: mapStatus(asString(ev.stato_mappato) || rawStatus),
      note: asString(ev.note) || undefined
    } as TrackingEvent;
  }).filter((e): e is TrackingEvent => !!e && (!!e.date || !!e.rawStatus));
  // The first event in the array is typically the most recent (per provided example)
  const oldest = events[events.length - 1];
  const pickupLocation = oldest ? oldest.location : undefined;
  const numeroGiacenza = asString(root.numero_giacenza);
  const dataApertura = asString(root.data_apertura_giacenza);
  const giacenza = numeroGiacenza && numeroGiacenza !== 'null' ? {
    numero: numeroGiacenza,
    apertura: dataApertura,
    motivazione: asString(root.motivazione) || undefined,
    dispoRequired: root.giacenza_disposizioni_required === true
  } : null;
  const trackingRecovered = asString(root.n_spedizione_recuperato) || undefined;
  const nSpedizione = asString(root.n_spedizione) || tracking;
  return {
    id: `SP-${nSpedizione.slice(-6).toUpperCase()}`,
    carrierId,
    carrierName,
    tracking: nSpedizione,
    trackingRecovered: trackingRecovered && trackingRecovered !== nSpedizione ? trackingRecovered : undefined,
    status: asString(root.last_status) || (events[0] ? events[0].rawStatus : 'Sconosciuto'),
    statusDate: asString(root.last_status_data),
    statusTime: asString(root.last_status_ora),
    delivered: root.consegnato === true,
    isReturn: root.reso === true,
    giacenza,
    pickupLocation,
    events,
    raw: payload
  };
}
const STATUS_STYLES: Record<MappedStatus, {
  bg: string;
  text: string;
  border: string;
  dot: string;
}> = {
  CONSEGNATA: {
    bg: 'bg-[#D4FF3A]/10',
    text: 'text-[#D4FF3A]',
    border: 'border-[#D4FF3A]/30',
    dot: 'bg-[#D4FF3A]'
  },
  IN_CONSEGNA: {
    bg: 'bg-[#5DA9FF]/10',
    text: 'text-[#5DA9FF]',
    border: 'border-[#5DA9FF]/30',
    dot: 'bg-[#5DA9FF]'
  },
  IN_TRANSITO: {
    bg: 'bg-[#F5A524]/10',
    text: 'text-[#F5A524]',
    border: 'border-[#F5A524]/30',
    dot: 'bg-[#F5A524]'
  },
  TRASMESSO: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    dot: 'bg-[#6B6B6B]'
  },
  GIACENZA: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    dot: 'bg-[#EF4444]'
  },
  RESO: {
    bg: 'bg-[#EF4444]/10',
    text: 'text-[#EF4444]',
    border: 'border-[#EF4444]/30',
    dot: 'bg-[#EF4444]'
  },
  ALTRO: {
    bg: 'bg-[#1F1F1F]',
    text: 'text-[#9A9A9A]',
    border: 'border-[#262626]',
    dot: 'bg-[#9A9A9A]'
  }
};
export function NewShipmentPage({
  onClose,
  onCreated
}: {
  onClose: () => void;
  onCreated?: (shipmentId: string) => void;
}) {
  const [phase, setPhase] = useState<Phase>('input');
  const [carrierId, setCarrierId] = useState<string>('fedex');
  const [tracking, setTracking] = useState('');
  const [loadingStep, setLoadingStep] = useState(0);
  const [shipment, setShipment] = useState<LoadedShipment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const carrier = CARRIERS.find(c => c.id === carrierId)!;
  const isBRT = carrier.id === 'brt';
  const canSubmit = !!carrierId && tracking.trim().length >= 6 && isBRT;
  useEffect(() => {
    if (phase !== 'loading') return;
    let cancelled = false;
    setLoadingStep(0);
    const interval = setInterval(() => {
      setLoadingStep(s => Math.min(s + 1, LOADING_STEPS.length - 1));
    }, 600);
    const fetchTracking = async () => {
      try {
        const response = await fetch(AISAC_TRACKING_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token: AISAC_TOKEN,
            corriere: 'BRT',
            tracking: tracking.trim()
          })
        });
        if (!response.ok) {
          throw new Error('Servizio tracking non disponibile (HTTP ' + response.status + ').');
        }
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        const root = data && typeof data === 'object' ? data as Record<string, unknown> : {};
        if (root.esito === false) {
          const reason = typeof root.motivazione === 'string' ? root.motivazione as string : typeof root.error === 'string' ? root.error as string : 'Tracking non trovato per il corriere selezionato.';
          setError(reason);
          setPhase('input');
          return;
        }
        const mapped = mapApiResponse(data, carrier.id, carrier.name, tracking.trim());
        setShipment(mapped);
        setPhase('preview');
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Impossibile contattare il servizio di tracking.';
        setError(msg);
        setPhase('input');
      }
    };
    fetchTracking();
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [phase, carrier, tracking]);
  const handleFetch = () => {
    if (!canSubmit) return;
    setError(null);
    setPhase('loading');
  };
  const handleConfirm = () => {
    if (!shipment) return;
    // Map the API-loaded tracking data into the table's Shipment shape.
    const statusMap: Record<string, Status> = {
      CONSEGNATA: 'Consegnata',
      IN_CONSEGNA: 'In consegna',
      IN_TRANSITO: 'In transito',
      TRASMESSO: 'In partenza',
      GIACENZA: 'Anomalia',
      RESO: 'Anomalia',
      ALTRO: 'In transito'
    };
    const latest = shipment.events[0];
    const mappedStatus: Status = latest ? statusMap[latest.mappedStatus] ?? 'In transito' : 'In transito';
    const now = new Date();
    const itMonth = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'][now.getMonth()];
    const todayLabel = `${String(now.getDate()).padStart(2, '0')} ${itMonth} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const newRow: Shipment = {
      id: shipment.id,
      trackingId: shipment.tracking,
      store: shipment.pickupLocation || 'Magazzino Centrale',
      address: '—',
      city: latest?.location || '—',
      carrier: shipment.carrierName,
      colli: 1,
      weight: '—',
      status: shipment.delivered ? 'Consegnata' : mappedStatus,
      signed: shipment.delivered ? 'ok' : 'none',
      cost: '—',
      departure: todayLabel,
      eta: shipment.statusDate ? `${shipment.statusDate}${shipment.statusTime ? ' ' + shipment.statusTime : ''}` : todayLabel,
      reference: shipment.trackingRecovered || shipment.tracking,
      contact: {
        name: '—',
        phone: '—',
        email: '—'
      },
      // Build the RouteStep[] used by the detail page progress bar.
      // API events are returned newest-first; reverse so the timeline
      // reads oldest → newest, marking the most recent as 'current' and
      // every prior event as 'completed'. If the shipment is fully
      // delivered we mark everything as completed.
      steps: (() => {
        const chronological = [...shipment.events].reverse();
        return chronological.map((e, idx) => {
          const isLast = idx === chronological.length - 1;
          return {
            label: capitalizeFirst(e.rawStatus),
            location: e.location || '—',
            timestamp: `${e.date}${e.time ? ' · ' + e.time : ''}`,
            status: shipment.delivered || !isLast ? 'completed' : 'current'
          };
        });
      })(),
      events: shipment.events.map((e, idx) => {
        const t: 'info' | 'warning' | 'success' | 'pickup' = e.mappedStatus === 'CONSEGNATA' ? 'success' : e.mappedStatus === 'GIACENZA' || e.mappedStatus === 'RESO' ? 'warning' : idx === shipment.events.length - 1 ? 'pickup' : 'info';
        return {
          timestamp: e.time || '—',
          date: e.date,
          location: e.location || '—',
          description: e.rawStatus,
          type: t
        };
      }),
      apiData: isBRT ? {
        source: 'brt-live',
        currentPhase: capitalizeFirst(shipment.status),
        currentPhaseRaw: shipment.status,
        lastStatusDate: shipment.statusDate,
        lastStatusTime: shipment.statusTime,
        delivered: shipment.delivered,
        isReturn: shipment.isReturn,
        trackingRecovered: shipment.trackingRecovered,
        giacenza: shipment.giacenza ?? undefined
      } : undefined
    };
    addShipment(newRow);
    onCreated?.(shipment.id);
    setPhase('done');
  };
  return <div className="fixed inset-0 z-50 bg-[#0A0A0A] overflow-y-auto" data-id="element-2089">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-id="element-2090">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-2091">
          <div data-id="element-2092">
            <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-2093">
              Nuova spedizione
            </h1>
            <p className="text-sm text-[#9A9A9A] mt-1" data-id="element-2094">
              Inserisci il tracking e il corriere — i dati verranno recuperati
              automaticamente.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg text-[#9A9A9A] hover:text-[#FAFAFA] hover:bg-[#181818] transition shrink-0" data-id="element-2095">
            <X className="h-5 w-5" data-id="element-2096" />
          </button>
        </div>

        {phase === 'input' && <InputPhase carrierId={carrierId} onCarrierChange={setCarrierId} tracking={tracking} onTrackingChange={setTracking} error={error} onSubmit={handleFetch} canSubmit={canSubmit} carrierPlaceholder={carrier.placeholder} isBRT={isBRT} carrierName={carrier.name} data-id="element-2097" />}

        {phase === 'loading' && <LoadingPhase carrier={carrier} tracking={tracking} step={loadingStep} data-id="element-2098" />}

        {phase === 'preview' && shipment && <PreviewPhase shipment={shipment} onConfirm={handleConfirm} onBack={() => setPhase('input')} data-id="element-2099" />}

        {phase === 'done' && shipment && <DonePhase shipment={shipment} onClose={onClose} data-id="element-2100" />}
      </div>
    </div>;
}
function InputPhase({
  carrierId,
  onCarrierChange,
  tracking,
  onTrackingChange,
  error,
  onSubmit,
  canSubmit,
  carrierPlaceholder,
  isBRT,
  carrierName
}: {
  carrierId: string;
  onCarrierChange: (id: string) => void;
  tracking: string;
  onTrackingChange: (v: string) => void;
  error: string | null;
  onSubmit: () => void;
  canSubmit: boolean;
  carrierPlaceholder: string;
  isBRT: boolean;
  carrierName: string;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-5 space-y-6" data-id="element-2101">
      <div data-id="element-2102">
        <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-2.5" data-id="element-2103">
          Corriere <span className="text-[#D4FF3A] ml-0.5" data-id="element-2104">*</span>
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-id="element-2105">
          {CARRIERS.map(c => {
          const selected = c.id === carrierId;
          return <button key={c.id} type="button" onClick={() => onCarrierChange(c.id)} className={`relative p-3 rounded-lg border transition flex flex-col items-center justify-center gap-1.5 ${selected ? 'bg-[#D4FF3A]/5 border-[#D4FF3A]/40' : 'bg-[#0A0A0A] border-[#262626] hover:border-[#333]'}`} data-id="element-2106">
                <div className="h-8 w-12 bg-white rounded p-1 flex items-center justify-center" data-id="element-2107">
                  <img src={c.logo} alt={c.name} className="max-w-full max-h-full object-contain" data-id="element-2108" />
                </div>
                <span className={`text-[11px] font-medium ${selected ? 'text-[#FAFAFA]' : 'text-[#9A9A9A]'}`} data-id="element-2109">
                  {c.name}
                </span>
                {selected && <Check className="absolute top-1.5 right-1.5 h-3 w-3 text-[#D4FF3A]" data-id="element-2110" />}
              </button>;
        })}
        </div>
        {!isBRT && <div className="mt-3 flex items-start gap-2 p-3 bg-[#F5A524]/5 border border-[#F5A524]/30 rounded-lg" data-id="element-2111">
            <AlertCircle className="h-4 w-4 text-[#F5A524] shrink-0 mt-0.5" data-id="element-2112" />
            <p className="text-xs text-[#FAFAFA]" data-id="element-2113">
              L'integrazione di tracking automatico è attualmente disponibile{' '}
              <span className="font-semibold" data-id="element-2114">solo per BRT</span>. Per{' '}
              {carrierName} arriverà a breve — seleziona BRT per recuperare i
              dati.
            </p>
          </div>}
      </div>

      <div data-id="element-2115">
        <label className="block text-[10px] uppercase tracking-wider text-[#9A9A9A] font-medium mb-1.5" data-id="element-2116">
          Codice tracking <span className="text-[#D4FF3A] ml-0.5" data-id="element-2117">*</span>
        </label>
        <div className="relative" data-id="element-2118">
          <Search className="h-4 w-4 text-[#6B6B6B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2119" />
          <input type="text" value={tracking} onChange={e => onTrackingChange(e.target.value.toUpperCase())} placeholder={`Es. ${carrierPlaceholder}`} onKeyDown={e => e.key === 'Enter' && canSubmit && onSubmit()} autoFocus className="w-full pl-9 pr-3 py-2.5 bg-[#0A0A0A] border border-[#262626] rounded-lg text-sm font-mono text-[#FAFAFA] placeholder:text-[#6B6B6B] focus:outline-none focus:border-[#D4FF3A]/40" data-id="element-2120" />
        </div>
        <p className="text-[11px] text-[#6B6B6B] mt-1.5" data-id="element-2121">
          Inserisci il codice fornito dal corriere — recupereremo destinatario,
          origine, peso ed eventi di tracking.
        </p>
      </div>

      {error && <div className="flex items-start gap-2 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg" data-id="element-2122">
          <AlertCircle className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" data-id="element-2123" />
          <p className="text-xs text-[#EF4444]" data-id="element-2124">{error}</p>
        </div>}

      <div className="pt-2 flex items-center justify-end gap-2 border-t border-[#262626]" data-id="element-2125">
        <button type="button" onClick={onSubmit} disabled={!canSubmit} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition ${canSubmit ? 'bg-[#D4FF3A] text-[#0A0A0A] hover:bg-[#c2ed28]' : 'bg-[#1F1F1F] text-[#6B6B6B] cursor-not-allowed'}`} data-id="element-2126">
          Recupera dati spedizione
          <ArrowRight className="h-4 w-4" data-id="element-2127" />
        </button>
      </div>
    </div>;
}
function LoadingPhase({
  carrier,
  tracking,
  step
}: {
  carrier: {
    name: string;
    logo: string;
  };
  tracking: string;
  step: number;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-8 flex flex-col items-center text-center" data-id="element-2128">
      <div className="relative h-16 w-16 mb-5" data-id="element-2129">
        <div className="absolute inset-0 rounded-full bg-[#D4FF3A]/10 animate-pulse" data-id="element-2130" />
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-2131">
          <Loader2 className="h-7 w-7 text-[#D4FF3A] animate-spin" data-id="element-2132" />
        </div>
      </div>
      <div className="text-base font-semibold text-[#FAFAFA] font-heading mb-1" data-id="element-2133">
        Recupero spedizione in corso
      </div>
      <div className="flex items-center gap-2 text-xs text-[#9A9A9A] mb-6" data-id="element-2134">
        <div className="h-5 w-7 bg-white rounded p-0.5 flex items-center justify-center" data-id="element-2135">
          <img src={carrier.logo} alt={carrier.name} className="max-w-full max-h-full object-contain" data-id="element-2136" />
        </div>
        <span data-id="element-2137">{carrier.name}</span>
        <span className="text-[#6B6B6B]" data-id="element-2138">·</span>
        <span className="font-mono" data-id="element-2139">{tracking}</span>
      </div>
      <ul className="space-y-2 text-left w-full max-w-sm" data-id="element-2140">
        {LOADING_STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return <li key={s} className={`flex items-center gap-2 text-xs ${done || active ? 'text-[#FAFAFA]' : 'text-[#6B6B6B]'}`} data-id="element-2141">
              {done ? <Check className="h-3.5 w-3.5 text-[#D4FF3A] shrink-0" data-id="element-2142" /> : active ? <Loader2 className="h-3.5 w-3.5 text-[#D4FF3A] animate-spin shrink-0" data-id="element-2143" /> : <span className="h-3.5 w-3.5 rounded-full border border-[#262626] shrink-0" data-id="element-2144" />}
              {s}
            </li>;
      })}
      </ul>
    </div>;
}
function PreviewPhase({
  shipment,
  onConfirm,
  onBack
}: {
  shipment: LoadedShipment;
  onConfirm: () => void;
  onBack: () => void;
}) {
  const lastStatusKey = mapStatus(shipment.status);
  const lastStyle = STATUS_STYLES[lastStatusKey];
  return <div className="space-y-4" data-id="element-2145">
      <div className="flex items-start gap-3 p-3 bg-[#D4FF3A]/5 border border-[#D4FF3A]/30 rounded-lg" data-id="element-2146">
        <CheckCircle2 className="h-4 w-4 text-[#D4FF3A] shrink-0 mt-0.5" data-id="element-2147" />
        <div data-id="element-2148">
          <p className="text-sm text-[#FAFAFA] font-medium" data-id="element-2149">
            Spedizione trovata
          </p>
          <p className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-2150">
            Dati ricevuti in tempo reale dal corriere. Conferma per aggiungerla
            alla lista.
          </p>
        </div>
      </div>

      <div className="bg-[#181818] border border-[#262626] rounded-xl overflow-hidden" data-id="element-2151">
        <div className="px-5 py-4 border-b border-[#262626] flex items-center justify-between gap-3 flex-wrap" data-id="element-2152">
          <div className="min-w-0" data-id="element-2153">
            <div className="text-xs font-mono text-[#9A9A9A]" data-id="element-2154">Tracking</div>
            <div className="text-base font-semibold text-[#FAFAFA] font-mono truncate" data-id="element-2155">
              {shipment.tracking}
            </div>
            {shipment.trackingRecovered && <div className="text-[11px] font-mono text-[#6B6B6B] mt-0.5 truncate" data-id="element-2156">
                Recuperato: {shipment.trackingRecovered}
              </div>}
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border ${lastStyle.bg} ${lastStyle.text} ${lastStyle.border}`} data-id="element-2157">
            <span className={`h-1.5 w-1.5 rounded-full ${lastStyle.dot}`} data-id="element-2158" />
            {shipment.status}
          </span>
        </div>

        <div className="p-5 space-y-4" data-id="element-2159">
          <PreviewRow icon={<Truck className="h-4 w-4 text-[#D4FF3A]" data-id="element-2161" />} label="Corriere" value={shipment.carrierName} data-id="element-2160" />
          <PreviewRow icon={<Clock className="h-4 w-4 text-[#D4FF3A]" data-id="element-2163" />} label="Ultimo aggiornamento" value={shipment.statusDate ? `${shipment.statusDate}${shipment.statusTime ? ' · ' + shipment.statusTime : ''}` : '—'} secondary={shipment.status} data-id="element-2162" />
          {shipment.pickupLocation && <PreviewRow icon={<MapPin className="h-4 w-4 text-[#D4FF3A]" data-id="element-2165" />} label="Luogo di ritiro" value={shipment.pickupLocation} data-id="element-2164" />}
          {shipment.isReturn && <PreviewRow icon={<RotateCcw className="h-4 w-4 text-[#EF4444]" data-id="element-2167" />} label="Tipo" value="Reso" secondary="Spedizione segnalata come reso dal corriere." data-id="element-2166" />}
          {shipment.giacenza && <div className="rounded-lg border border-[#EF4444]/30 bg-[#EF4444]/5 p-3" data-id="element-2168">
              <div className="flex items-start gap-2" data-id="element-2169">
                <PackageX className="h-4 w-4 text-[#EF4444] shrink-0 mt-0.5" data-id="element-2170" />
                <div className="min-w-0" data-id="element-2171">
                  <div className="text-xs font-semibold text-[#EF4444] uppercase tracking-wider" data-id="element-2172">
                    In giacenza
                  </div>
                  <div className="text-xs text-[#FAFAFA] mt-1" data-id="element-2173">
                    N. giacenza{' '}
                    <span className="font-mono" data-id="element-2174">
                      {shipment.giacenza.numero}
                    </span>
                    {shipment.giacenza.apertura && <> · aperta il {shipment.giacenza.apertura}</>}
                  </div>
                  {shipment.giacenza.motivazione && <div className="text-[11px] text-[#9A9A9A] mt-1" data-id="element-2175">
                      {shipment.giacenza.motivazione}
                    </div>}
                  {shipment.giacenza.dispoRequired && <div className="text-[11px] text-[#F5A524] mt-1 font-medium" data-id="element-2176">
                      Disposizioni richieste al corriere.
                    </div>}
                </div>
              </div>
            </div>}
        </div>
      </div>

      {/* Timeline */}
      {shipment.events.length > 0 && <div className="bg-[#181818] border border-[#262626] rounded-xl p-5" data-id="element-2177">
          <div className="flex items-center justify-between mb-3" data-id="element-2178">
            <h3 className="text-sm font-semibold text-[#FAFAFA] font-heading" data-id="element-2179">
              Storico spedizione
            </h3>
            <span className="text-[11px] text-[#6B6B6B]" data-id="element-2180">
              {shipment.events.length} eventi
            </span>
          </div>
          <ol className="relative" data-id="element-2181">
            <span aria-hidden className="absolute left-2 top-2 bottom-2 w-px bg-[#262626]" data-id="element-2182" />
            {shipment.events.map((e, idx) => {
          const isLatest = idx === 0;
          return <li key={idx} className="relative pl-7 pb-4 last:pb-0" data-id="element-2183">
                  {isLatest ? <span className="absolute left-0 top-0.5 h-4 w-4 rounded-full flex items-center justify-center border-2 border-[#D4FF3A] bg-[#0A0A0A]" data-id="element-2184">
                      <span className="h-1 w-1 rounded-full bg-[#D4FF3A]" data-id="element-2185" />
                    </span> : <span className="absolute left-0 top-0.5 h-4 w-4 rounded-full flex items-center justify-center bg-[#D4FF3A]" data-id="element-2186">
                      <Check className="h-2.5 w-2.5 text-[#0A0A0A]" strokeWidth={3.5} data-id="element-2187" />
                    </span>}
                  <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-2188">
                    <span className="text-sm font-medium text-[#FAFAFA]" data-id="element-2189">
                      {capitalizeFirst(e.rawStatus)}
                    </span>
                    {isLatest && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-[#D4FF3A]/10 text-[#D4FF3A] border border-[#D4FF3A]/30" data-id="element-2190">
                        In corso
                      </span>}
                  </div>
                  <div className="text-[11px] font-mono text-[#6B6B6B] mt-0.5" data-id="element-2191">
                    {e.date}
                    {e.time && <> · {e.time}</>}
                    {e.location && <span className="text-[#9A9A9A]" data-id="element-2192"> · {e.location}</span>}
                  </div>
                  {e.note && <div className="text-[11px] text-[#9A9A9A] mt-1" data-id="element-2193">
                      {e.note}
                    </div>}
                </li>;
        })}
          </ol>
        </div>}

      <div className="flex items-center justify-end gap-2" data-id="element-2194">
        <button type="button" onClick={onBack} className="px-3.5 py-2 rounded-lg bg-[#181818] border border-[#262626] text-[#FAFAFA] text-sm font-medium hover:border-[#333] transition" data-id="element-2195">
          Indietro
        </button>
        <button type="button" onClick={onConfirm} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-2196">
          <Check className="h-4 w-4" data-id="element-2197" />
          Aggiungi spedizione
        </button>
      </div>
    </div>;
}
function PreviewRow({
  icon,
  label,
  value,
  secondary
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  secondary?: string;
}) {
  return <div className="flex items-start gap-3" data-id="element-2198">
      <div className="h-8 w-8 rounded-lg bg-[#0A0A0A] border border-[#262626] flex items-center justify-center shrink-0" data-id="element-2199">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-2200">
        <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-2201">
          {label}
        </div>
        <div className="text-sm text-[#FAFAFA] mt-0.5" data-id="element-2202">{value}</div>
        {secondary && <div className="text-xs text-[#9A9A9A] mt-0.5" data-id="element-2203">{secondary}</div>}
      </div>
    </div>;
}
function DonePhase({
  shipment,
  onClose
}: {
  shipment: LoadedShipment;
  onClose: () => void;
}) {
  return <div className="bg-[#181818] border border-[#262626] rounded-xl p-8 flex flex-col items-center text-center" data-id="element-2204">
      <div className="h-14 w-14 rounded-full bg-[#D4FF3A]/15 border border-[#D4FF3A]/40 flex items-center justify-center mb-4" data-id="element-2205">
        <Check className="h-6 w-6 text-[#D4FF3A]" data-id="element-2206" />
      </div>
      <h2 className="text-lg font-semibold text-[#FAFAFA] font-heading mb-1" data-id="element-2207">
        Spedizione aggiunta
      </h2>
      <p className="text-sm text-[#9A9A9A] mb-1" data-id="element-2208">
        {shipment.carrierName} ·{' '}
        <span className="font-mono" data-id="element-2209">{shipment.tracking}</span>
      </p>
      <p className="text-xs font-mono text-[#6B6B6B] mb-6" data-id="element-2210">{shipment.id}</p>

      <div className="flex items-center gap-2" data-id="element-2211">
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg bg-[#D4FF3A] text-[#0A0A0A] text-sm font-semibold hover:bg-[#c2ed28] transition" data-id="element-2212">
          Chiudi
        </button>
      </div>
    </div>;
}